import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { ErrorMessage } from 'shared/ErrorMessage';
import { AdvertiserNode } from 'typeDeclarations/graphql/nodes';
import { isNull } from 'typeDeclarations/typeGuards';
import { resolveBackgroundLocation } from '../utils';
import { VideoManagerContext, VideoManagerContextValue } from './VideoManagerContext';

const VIDEO_MANAGER_PROVIDER_QUERY = gql(/* GraphQL */ `
  query videoManagerProviderQuery($advertiserPrettyId: String) {
    advertiser(_id: $advertiserPrettyId) {
      id
      _id
      name
    }
  }
`);

type VideoManagerProviderQueryVariables = {
  advertiserPrettyId?: string;
};

type VideoManagerProviderQueryData = {
  advertiser: null | Pick<AdvertiserNode, 'id' | '_id' | 'name'>;
};

export const VideoManagerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { advertiserPrettyId } = useParams<{ advertiserPrettyId: string }>();
  const location = useLocation();
  const { pathname } = location;
  const backgroundLocation = resolveBackgroundLocation(location);

  const {
    data: queryData,
    error: queryError,
    loading: queryLoading,
  } = useQuery<VideoManagerProviderQueryData, VideoManagerProviderQueryVariables>(VIDEO_MANAGER_PROVIDER_QUERY, {
    fetchPolicy: 'network-only',
    variables: { advertiserPrettyId },
  });

  if (queryLoading) {
    return;
  }

  if (queryError || !queryData) return <ErrorMessage />;

  const { advertiser } = queryData;

  if (isNull(advertiser)) return <Navigate to="/not-found" replace />;

  const { id, _id } = advertiser;

  const contextValue: VideoManagerContextValue = {
    advertiserPrettyId: _id,
    advertiserUglyId: id,
    pathname,
    // Always rebuilt from the resolved background, so it also works for typed/refreshed URLs
    state: backgroundLocation ? { backgroundLocation } : undefined,
    backgroundLocation,
  };

  return <VideoManagerContext.Provider value={contextValue}>{children}</VideoManagerContext.Provider>;
};
